import type { QueryClient } from '@tanstack/react-query';

import { cleanArray } from '@/utils/cleanArray';

/**
 * 결제/구독 도메인 React Query key 중앙 관리.
 *
 * Supabase studio(`apps/studio/data/<domain>/keys.ts`) 패턴 이식 — key 문자열을
 * hook에 인라인하지 않고 한 곳에서 정의해, "서로 엮인" 결제/구독 query를 결합
 * invalidate할 때(아래 invalidateBillingQueries) 오탈자·누락 없이 안전하게 나열한다.
 *
 * ⚠️ 식별자 불일치(레거시): subscription/reserved/billings/user는 email 기준,
 * annualEligibility/billingKeys는 userId 기준으로 캐시된다. 캐시 identity를 함부로
 * 바꾸면 전 호출처가 깨지므로, 팩토리는 각 key가 쓰던 식별자를 그대로 받는다.
 */
export const billingKeys = {
  /** GET /users/:id/subscription — 현재 구독 (email 기준) */
  subscription: (email?: string) => cleanArray(['subscription', email]),
  /** GET /subscriptions/reserved — 다음 예약 구독 (email 기준) */
  reservedSubscription: (email?: string) => cleanArray(['reserved', 'subscription', email]),
  /** GET /payments/annual-eligibility — 연간 배너 상태 (userId 기준) */
  annualEligibility: (userId?: string) => cleanArray(['annual-eligibility', userId]),
  /** GET /users/:id/billing-keys — 등록 결제수단 (userId 기준) */
  billingKeysList: (userId?: string) => ['billing_keys', userId] as const,

  /** GET /billings — 결제 내역 (email + 페이지네이션) */
  billings: (email?: string, page?: number, limit?: number, status?: string | string[]) =>
    cleanArray(['billings', email, page, limit, status || '']),
  /** 결제 내역 전체 prefix — 페이지네이션 무관 일괄 invalidate용 */
  billingsAll: () => ['billings'] as const,

  /** useBillingPing — 최근 결제 폴링 단건 */
  billingPing: () => ['billing'] as const,
  /** useBillingReservedPing — 예약 결제 폴링 단건 */
  billingReservedPing: () => ['billing', 'reserved'] as const,
  /** useBillingScheduleCancelled — 예약취소 결제 단건 */
  billingScheduleCancelled: () => ['billing', 'scheduled-cancelled'] as const,
  /** GET /billings/scheduled — 살아있는 다음 결제 예약 단건 */
  scheduledBilling: () => ['billing', 'scheduled'] as const,
  /** useBilling 계열 단건 조회들의 공통 prefix (billing / billing,reserved / billing,scheduled-cancelled) */
  billingGroupAll: () => ['billing'] as const,

  /** GET /user/me — 구독 정보가 함께 실려오므로 구독 변경 시 같이 갱신 (email 기준) */
  user: (email?: string) => cleanArray([email, 'user']),
} as const;

interface BillingIdentifiers {
  email?: string;
  userId?: string;
}

/**
 * 구독 상태가 바뀌는 mutation(구독/요금제 변경/해지/결제수단 변경/결제 성공) 성공 후 호출.
 * 서로 엮인 결제·구독 query를 한 번에 무효화한다.
 *
 * ⚠️ BE 반영 지연 대비(`delayMs`): PortOne/webhook 반영은 결제 직후 1~2초 stale할 수 있어,
 * 성공 직후 즉시 refetch하면 옛 상태를 다시 "fresh"로 굳혀버린다(= 새로고침해야 정상 버그의 원인).
 * Supabase도 billing mutation에서 동일하게 setTimeout 후 invalidate한다
 * (`org-subscription-update-mutation.ts`). 정산이 필요한 경로는 delayMs를 준다.
 *
 * 이 헬퍼는 결합 invalidate 전용이며, shops/feature 등 도메인 밖 갱신이 필요한 mutation은
 * 기존 blanket invalidate를 함께 유지한다.
 */
export const invalidateBillingQueries = async (
  queryClient: QueryClient,
  { email, userId }: BillingIdentifiers,
  options?: { delayMs?: number },
) => {
  if (options?.delayMs) {
    await new Promise((resolve) => setTimeout(resolve, options.delayMs));
  }

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: billingKeys.subscription(email) }),
    queryClient.invalidateQueries({ queryKey: billingKeys.reservedSubscription(email) }),
    queryClient.invalidateQueries({ queryKey: billingKeys.annualEligibility(userId) }),
    queryClient.invalidateQueries({ queryKey: billingKeys.billingKeysList(userId) }),
    queryClient.invalidateQueries({ queryKey: billingKeys.billingsAll() }),
    queryClient.invalidateQueries({ queryKey: billingKeys.billingGroupAll() }),
    queryClient.invalidateQueries({ queryKey: billingKeys.user(email) }),
  ]);
};
